'use client';

/**
 * ProjectConnectionsTab
 *
 * Connections tab content for project page.
 * Shows linked connections and allows linking new ones.
 */

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Center,
  Inline,
  Spinner,
  Stack,
  Text,
} from '@cortex/ui';
import {
  AlertCircle,
  CheckCircle2,
  Cloud,
  GitBranch,
  Link2Off,
  Plus,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

import { LinkConnectionModal } from './LinkConnectionModal';

interface Connection {
  id: string;
  provider_type: string;
  provider_id: string;
  label: string;
  description: string | null;
  validation_status: string | null;
  last_validated_at: string | null;
  metadata: Record<string, unknown>;
  config_overrides?: Record<string, unknown>;
}

interface ProjectConnectionsTabProps {
  projectId: string;
  organizationId: string;
}

export function ProjectConnectionsTab({
  projectId,
  organizationId,
}: ProjectConnectionsTabProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  // Fetch project connections
  const fetchConnections = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `/admin/projects/${projectId}/connections`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch connections');
      }

      const { data } = await response.json();
      setConnections(data || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load connections'
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, fetchWithAuth]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Get provider icon
  const getProviderIcon = (providerType: string) => {
    switch (providerType) {
      case 'git':
        return GitBranch;
      case 'preview':
        return Cloud;
      default:
        return Link2Off;
    }
  };

  // Get validation status badge
  const getValidationBadge = (status: string | null) => {
    switch (status) {
      case 'valid':
        return (
          <Badge variant="success">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Valid
          </Badge>
        );
      case 'invalid':
        return (
          <Badge variant="destructive">
            <AlertCircle className="mr-1 h-3 w-3" />
            Invalid
          </Badge>
        );
      case 'expired':
        return (
          <Badge variant="warning">
            <AlertCircle className="mr-1 h-3 w-3" />
            Expired
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            <AlertCircle className="mr-1 h-3 w-3" />
            Unknown
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <Center minHeight="12rem">
        <Spinner size="md" variant="muted" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <Text weight="medium" size="sm">
            Failed to load connections
          </Text>
          <Text color="muted" size="sm" className="mt-1">
            {error}
          </Text>
        </AlertDescription>
      </Alert>
    );
  }

  if (connections.length === 0) {
    return (
      <>
        <Stack gap="md" align="center" className="py-12 text-center">
          <Link2Off className="text-foreground/60 mb-4 h-12 w-12" />
          <Text weight="medium" size="lg" className="mb-2">
            No connections linked
          </Text>
          <Text color="muted" size="sm" className="mb-4 max-w-md">
            Link a connection to enable Git integration, preview deployments,
            and other features.
          </Text>
          <Button onClick={() => setLinkModalOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Link Connection
          </Button>
        </Stack>

        <LinkConnectionModal
          open={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          projectId={projectId}
          organizationId={organizationId}
          linkedConnectionIds={[]}
          onLinked={fetchConnections}
        />
      </>
    );
  }

  // Get linked connection IDs for the modal
  const linkedConnectionIds = connections.map((c) => c.id);

  return (
    <Stack gap="md">
      <Inline justify="between">
        <Text color="muted" size="sm">
          Connections linked to this project for Git, preview deployments, and
          other integrations.
        </Text>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setLinkModalOpen(true)}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Link Connection
        </Button>
      </Inline>

      <Stack
        className="divide-border border-border divide-y rounded-lg border"
        gap="none"
      >
        {connections.map((connection) => {
          const Icon = getProviderIcon(connection.provider_type);

          return (
            <Stack key={connection.id} className="p-4" gap="none">
              <Inline justify="between">
                <Inline gap="md">
                  <Center className="bg-surface-2 h-10 w-10 rounded-lg">
                    <Icon className="text-foreground/70 h-5 w-5" />
                  </Center>
                  <Stack gap="none">
                    <Inline gap="xs">
                      <Text weight="medium">{connection.label}</Text>
                      <Badge variant="outline">{connection.provider_id}</Badge>
                    </Inline>
                    {connection.description && (
                      <Text color="muted" size="sm" className="mt-0.5">
                        {connection.description}
                      </Text>
                    )}
                  </Stack>
                </Inline>

                <Inline gap="sm">
                  {getValidationBadge(connection.validation_status)}
                </Inline>
              </Inline>
            </Stack>
          );
        })}
      </Stack>

      <LinkConnectionModal
        open={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        projectId={projectId}
        organizationId={organizationId}
        linkedConnectionIds={linkedConnectionIds}
        onLinked={fetchConnections}
      />
    </Stack>
  );
}
